import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Alert,
  Skeleton,
} from '@mui/material';
import { Kitchen, ArrowForward } from '@mui/icons-material';
import { useRecipes } from '../../contexts/RecipeContext';
import { CATEGORIES } from '../../config';

function matchIngredients(recipeIngredients, pantryItems) {
  const inStockItems = pantryItems.filter((p) => p.inStock);
  if (!recipeIngredients?.length) return { missing: [], present: [] };

  const missing = [];
  const present = [];

  for (const ing of recipeIngredients) {
    const ingName = ing.name?.toLowerCase() || '';
    const found = inStockItems.some(
      (p) =>
        p.name.toLowerCase().includes(ingName) ||
        ingName.includes(p.name.toLowerCase())
    );
    if (found) {
      present.push(ing);
    } else {
      missing.push(ing);
    }
  }

  return { missing, present };
}

function RecipeMatchCard({ recipe, missing, navigate }) {
  const category = CATEGORIES.find((c) => c.value === recipe.category);

  return (
    <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>{recipe.title}</Typography>
          {category && (
            <Typography variant="caption" color="text.secondary">
              {category.emoji} {category.label}
            </Typography>
          )}
        </Box>
        {missing.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Missing:</Typography>
            {missing.map((ing, i) => (
              <Chip
                key={i}
                label={ing.name}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.68rem',
                  bgcolor: 'rgba(239,83,80,0.1)',
                  color: 'error.main',
                  border: '1px solid rgba(239,83,80,0.2)',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
      <Button
        size="small"
        endIcon={<ArrowForward />}
        onClick={() => navigate(`/recipes/${recipe.id}`)}
        sx={{ flexShrink: 0, color: 'primary.main' }}
      >
        View
      </Button>
    </Paper>
  );
}

function Section({ emoji, title, color, recipes, navigate, showMissing }) {
  if (recipes.length === 0) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem' }}>{emoji}</Typography>
        <Typography variant="h6">{title}</Typography>
        <Chip
          label={recipes.length}
          size="small"
          sx={{ bgcolor: `${color}20`, color, border: `1px solid ${color}40`, height: 22 }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {recipes.map(({ recipe, missing }) => (
          <RecipeMatchCard
            key={recipe.id}
            recipe={recipe}
            missing={showMissing ? missing : []}
            navigate={navigate}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function WhatCanICookPage() {
  const navigate = useNavigate();
  const { recipes, pantry, pantryLoading, recipesLoading } = useRecipes();

  const hasPantryItems = pantry?.items?.some((i) => i.inStock);

  const categorized = useMemo(() => {
    if (!hasPantryItems) return { ready: [], almost: [], need: [] };

    const ready = [];
    const almost = [];
    const need = [];

    for (const recipe of recipes) {
      if (!recipe.ingredients?.length) {
        ready.push({ recipe, missing: [] });
        continue;
      }
      const { missing } = matchIngredients(recipe.ingredients, pantry.items || []);
      if (missing.length === 0) {
        ready.push({ recipe, missing: [] });
      } else if (missing.length <= 3) {
        almost.push({ recipe, missing });
      } else {
        need.push({ recipe, missing });
      }
    }

    return { ready, almost, need };
  }, [recipes, pantry, hasPantryItems]);

  if (recipesLoading || pantryLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" height={50} width="60%" />
        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} sx={{ mt: 2 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>What Can I Cook?</Typography>
        <Typography color="text.secondary">
          Based on what's in your pantry right now.
        </Typography>
      </Box>

      {recipes.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You haven't added any recipes yet.{' '}
          <Button size="small" onClick={() => navigate('/recipes/new')} sx={{ color: 'primary.main' }}>
            Add a recipe
          </Button>
        </Alert>
      )}

      {!hasPantryItems && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🧺</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>Your pantry is empty</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add ingredients to your pantry to see which recipes you can make.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Kitchen />}
            onClick={() => navigate('/pantry')}
            sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)' }}
          >
            Go to Pantry
          </Button>
        </Paper>
      )}

      {hasPantryItems && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              You have <strong>{pantry.items?.filter((i) => i.inStock).length}</strong> items in stock ·{' '}
              <Button size="small" onClick={() => navigate('/pantry')} sx={{ color: 'primary.main', p: 0, minWidth: 0 }}>
                Update pantry
              </Button>
            </Typography>
          </Box>

          {categorized.ready.length === 0 && categorized.almost.length === 0 && categorized.need.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '3rem', mb: 2 }}>🤔</Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>No matches found</Typography>
              <Typography color="text.secondary">
                Add more items to your pantry or more recipes to find matches.
              </Typography>
            </Paper>
          )}

          <Section
            emoji="✅"
            title="Ready to Cook"
            color="#66bb6a"
            recipes={categorized.ready}
            navigate={navigate}
            showMissing={false}
          />

          <Section
            emoji="🛒"
            title="Almost There"
            color="#ffa726"
            recipes={categorized.almost}
            navigate={navigate}
            showMissing={true}
          />

          <Section
            emoji="💡"
            title="Need Ingredients"
            color="#a89880"
            recipes={categorized.need}
            navigate={navigate}
            showMissing={true}
          />
        </>
      )}
    </Box>
  );
}
